import React, { useState, useEffect } from 'react';
import { Play, Pause, Clock } from 'lucide-react';
import { Task, supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { CommentModal } from './CommentModal';
import { stopTimerAndSave } from '../../lib/timerUtils';
import IconButton from '../ui/IconButton';

interface TaskTimerProps {
  task: Task;
  onUpdate: () => void;
}

export default function TaskTimer({ task, onUpdate }: TaskTimerProps) {
  const { user } = useAuth();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(task.timer_activo);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [timeLoggedForComment, setTimeLoggedForComment] = useState('');

  useEffect(() => {
    setIsRunning(task.timer_activo);

    if (task.timer_activo && task.timer_inicio) {
      const startTime = new Date(task.timer_inicio).getTime();
      const now = Date.now();
      setElapsedTime(Math.floor((now - startTime) / 1000));
    } else {
      setElapsedTime(0);
    }
  }, [task.timer_activo, task.timer_inicio]);

  useEffect(() => {
    let interval: number | undefined;

    if (isRunning && task.timer_inicio) {
      interval = window.setInterval(() => {
        const startTime = new Date(task.timer_inicio!).getTime();
        const now = Date.now();
        setElapsedTime(Math.floor((now - startTime) / 1000));
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning, task.timer_inicio]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = async () => {
    try {
      const { error } = await supabase
        .from('apro_tasks')
        .update({
          timer_activo: true,
          timer_inicio: new Date().toISOString(),
          ultimo_usuario_timer: user?.id,
        })
        .eq('id', task.id);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error('Error starting timer:', error);
      alert('Error al iniciar el timer');
    }
  };

  const handlePauseTimer = async () => {
    if (!task.timer_inicio) return;

    const startTime = new Date(task.timer_inicio).getTime();
    const now = Date.now();
    const millisWorked = now - startTime;
    const hours = Math.floor(millisWorked / (1000 * 60 * 60));
    const minutes = Math.floor((millisWorked % (1000 * 60 * 60)) / (1000 * 60));

    const timeLoggedStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    setTimeLoggedForComment(timeLoggedStr);
    setShowCommentModal(true);
  };

  const handleSaveComment = async (commentText: string) => {
    if (!task.timer_inicio || !user) return;

    try {
      const startedAt = new Date(task.timer_inicio);
      const endedAt   = new Date();
      const millisWorked = endedAt.getTime() - startedAt.getTime();
      const hours   = Math.floor(millisWorked / (1000 * 60 * 60));
      const minutes = Math.floor((millisWorked % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((millisWorked % (1000 * 60)) / 1000);
      const intervalString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      // Stop the timer and save the time entry (with manual-hours reconciliation)
      await stopTimerAndSave({
        taskId: task.id,
        timerInicio: task.timer_inicio,
        currentHorasEjecutadas: task.horas_ejecutadas,
        userId: user.id,
        description: commentText || null,
      });

      // Save the comment separately
      const { error: commentError } = await supabase
        .from('apro_comments')
        .insert([{
          user_id: user.id,
          task_id: task.id,
          comment_text: commentText,
          time_logged: intervalString,
        }]);

      if (commentError) throw commentError;

      setShowCommentModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error pausing timer:', error);
      alert('Error al pausar el timer');
    }
  };

  const handleSkipComment = async () => {
    if (!task.timer_inicio || !user) return;

    try {
      await stopTimerAndSave({
        taskId: task.id,
        timerInicio: task.timer_inicio,
        currentHorasEjecutadas: task.horas_ejecutadas,
        userId: user.id,
        description: null,
      });

      setShowCommentModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error pausing timer:', error);
      alert('Error al pausar el timer');
    }
  };

  const canControlTimer = !task.ultimo_usuario_timer || task.ultimo_usuario_timer === user?.id;

  return (
    <>
      <div className="flex items-center gap-2">
        {isRunning && (
          <div className="flex items-center gap-1 text-sm font-mono text-brand-600 bg-brand-50 px-2 py-1 rounded">
            <Clock className="w-4 h-4 animate-pulse" />
            <span>{formatTime(elapsedTime)}</span>
          </div>
        )}

        {isRunning ? (
          <IconButton
            icon={Pause}
            aria-label={canControlTimer ? "Pausar timer" : "Timer iniciado por otro usuario"}
            variant="ghost"
            onClick={handlePauseTimer}
            disabled={!canControlTimer}
            title={canControlTimer ? "Pausar timer" : "Timer iniciado por otro usuario"}
          />
        ) : (
          <IconButton
            icon={Play}
            aria-label="Iniciar timer"
            variant="ghost"
            onClick={handleStartTimer}
            title="Iniciar timer"
          />
        )}
      </div>

      <CommentModal
        isOpen={showCommentModal}
        onClose={handleSkipComment}
        onSave={handleSaveComment}
        timeLogged={timeLoggedForComment}
        taskName={task.nombre}
      />
    </>
  );
}
